import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Shield,
  MoreVertical,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

import { apiClient } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useAuth } from '../../providers/AuthProvider';
import { useToast } from '../../components/ui/Toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/Table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/Dialog';
import Checkbox from '../../components/ui/Checkbox';
import Label from '../../components/ui/Label';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

import type { Role, Permission, CreateRoleData, UpdateRoleData } from '../../types/auth';
import { PERMISSION_GROUPS } from '../../types/auth';

interface RoleFormData {
  name: string;
  displayNameEn: string;
  displayNameAr: string;
  level: number;
  description: string;
  permissions: Permission[];
  isActive: boolean;
}

const initialFormData: RoleFormData = {
  name: '',
  displayNameEn: '',
  displayNameAr: '',
  level: 1,
  description: '',
  permissions: [],
  isActive: true,
};

export default function RolesPage() {
  const { t, i18n } = useTranslation('users');
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState<RoleFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(Object.keys(PERMISSION_GROUPS)));

  const isRtl = i18n.dir() === 'rtl';

  // Fetch roles
  const {
    data: rolesData,
    isLoading: isLoadingRoles,
    error: rolesError,
  } = useQuery({
    queryKey: ['roles'],
    queryFn: () => apiClient.get<{ data: Role[] }>('/roles'),
  });

  const roles = rolesData?.data ?? [];

  // Filtered roles based on search
  const filteredRoles = useMemo(() => {
    if (!searchTerm) return roles;

    const lowerSearch = searchTerm.toLowerCase();
    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(lowerSearch) ||
        role.displayNameEn.toLowerCase().includes(lowerSearch) ||
        role.displayNameAr.includes(lowerSearch)
    );
  }, [roles, searchTerm]);

  // Mutations
  const createRoleMutation = useMutation({
    mutationFn: (data: CreateRoleData) => apiClient.post<Role>('/roles', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success(t('messages.roleCreateSuccess'));
      handleCloseCreateDialog();
    },
    onError: (error: Error) => {
      toast.error(t('common:error'), error.message);
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleData }) =>
      apiClient.put<Role>(`/roles/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success(t('messages.roleUpdateSuccess'));
      handleCloseEditDialog();
    },
    onError: (error: Error) => {
      toast.error(t('common:error'), error.message);
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/roles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success(t('messages.roleDeleteSuccess'));
      handleCloseDeleteDialog();
    },
    onError: (error: Error) => {
      toast.error(t('common:error'), error.message);
    },
  });

  // Form validation
  const validateForm = useCallback(
    (data: RoleFormData): Record<string, string> => {
      const errors: Record<string, string> = {};

      if (!data.name.trim()) {
        errors.name = t('validation.roleNameRequired');
      }

      if (!data.displayNameEn.trim()) {
        errors.displayNameEn = t('validation.displayNameEnRequired');
      }

      if (!data.displayNameAr.trim()) {
        errors.displayNameAr = t('validation.displayNameArRequired');
      }

      if (!data.level || data.level < 1) {
        errors.level = t('validation.levelRequired');
      }

      return errors;
    },
    [t]
  );

  // Handlers
  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setFormData(initialFormData);
    setFormErrors({});
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedRole(null);
    setFormData(initialFormData);
    setFormErrors({});
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedRole(null);
  };

  const handleOpenEditDialog = (role: Role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      displayNameEn: role.displayNameEn,
      displayNameAr: role.displayNameAr,
      level: role.level,
      description: role.description || '',
      permissions: role.permissions,
      isActive: role.isActive,
    });
    setIsEditDialogOpen(true);
  };

  const handleOpenDeleteDialog = (role: Role) => {
    setSelectedRole(role);
    setIsDeleteDialogOpen(true);
  };

  const handleCreateRole = () => {
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    createRoleMutation.mutate({
      name: formData.name,
      displayNameEn: formData.displayNameEn,
      displayNameAr: formData.displayNameAr,
      level: formData.level,
      description: formData.description,
      permissions: formData.permissions,
      isActive: formData.isActive,
    });
  };

  const handleUpdateRole = () => {
    if (!selectedRole) return;

    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    updateRoleMutation.mutate({
      id: selectedRole._id,
      data: {
        name: formData.name,
        displayNameEn: formData.displayNameEn,
        displayNameAr: formData.displayNameAr,
        level: formData.level,
        description: formData.description,
        permissions: formData.permissions,
        isActive: formData.isActive,
      },
    });
  };

  const handleDeleteRole = () => {
    if (!selectedRole) return;
    deleteRoleMutation.mutate(selectedRole._id);
  };

  const handlePermissionToggle = (permission: Permission) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const handleGroupToggle = (groupKey: string) => {
    const group = PERMISSION_GROUPS[groupKey as keyof typeof PERMISSION_GROUPS];
    const allSelected = group.permissions.every((p) => formData.permissions.includes(p));

    setFormData((prev) => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter((p) => !group.permissions.includes(p))
        : [...new Set([...prev.permissions, ...group.permissions])],
    }));
  };

  const handleExpandGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const handleSelectAllPermissions = () => {
    const allPermissions = Object.values(PERMISSION_GROUPS).flatMap((g) => g.permissions);
    setFormData((prev) => ({
      ...prev,
      permissions: allPermissions,
    }));
  };

  const handleDeselectAllPermissions = () => {
    setFormData((prev) => ({
      ...prev,
      permissions: [],
    }));
  };

  const getRoleDisplayName = (role: Role): string => {
    return i18n.language === 'ar' ? role.displayNameAr : role.displayNameEn;
  };

  const getGroupLabel = (groupKey: string): string => {
    const group = PERMISSION_GROUPS[groupKey as keyof typeof PERMISSION_GROUPS];
    return i18n.language === 'ar' ? group.labelAr : group.labelEn;
  };

  const getPermissionLabel = (permission: Permission): string => {
    const camelKey = permission.split(':').map((part, index) =>
      index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)
    ).join('');
    return t(`permissions.${camelKey}`, permission);
  };

  // Loading state
  if (isLoadingRoles) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state
  if (rolesError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <Shield className="h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {t('common:error')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {(rolesError as Error).message}
        </p>
      </div>
    );
  }

  const renderPermissionMatrix = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Label>{t('permissions.title')}</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSelectAllPermissions}
          >
            {t('permissions.selectAll')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleDeselectAllPermissions}
          >
            {t('permissions.deselectAll')}
          </Button>
        </div>
      </div>
      <div className="border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-200 dark:divide-gray-700">
        {Object.entries(PERMISSION_GROUPS).map(([groupKey, group]) => {
          const isExpanded = expandedGroups.has(groupKey);
          const allSelected = group.permissions.every((p) =>
            formData.permissions.includes(p)
          );
          const someSelected =
            !allSelected &&
            group.permissions.some((p) => formData.permissions.includes(p));

          return (
            <div key={groupKey}>
              <div
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 cursor-pointer"
                onClick={() => handleExpandGroup(groupKey)}
              >
                <div className="flex items-center gap-3">
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      id={`group-${groupKey}`}
                      checked={allSelected}
                      onCheckedChange={() => handleGroupToggle(groupKey)}
                      className={someSelected ? 'opacity-50' : ''}
                    />
                  </div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {getGroupLabel(groupKey)}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    ({group.permissions.filter((p) => formData.permissions.includes(p)).length}/
                    {group.permissions.length})
                  </span>
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                )}
              </div>
              {isExpanded && (
                <div className="p-3 space-y-2 ltr:pl-10 rtl:pr-10">
                  {group.permissions.map((permission) => (
                    <Checkbox
                      key={permission}
                      id={`permission-${permission}`}
                      label={getPermissionLabel(permission)}
                      checked={formData.permissions.includes(permission)}
                      onCheckedChange={() => handlePermissionToggle(permission)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('roles.title')}
        subtitle={t('roles.subtitle')}
        actions={
          hasPermission('roles:write') && (
            <Button
              onClick={() => setIsCreateDialogOpen(true)}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              {t('roles.addRole')}
            </Button>
          )
        }
      />

      <Card>
        <CardContent className="p-0">
          {/* Search Bar */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="relative max-w-md">
              <Input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search className="h-4 w-4" />}
              />
            </div>
          </div>

          {/* Roles Table */}
          {filteredRoles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Shield className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {t('roles.noRoles')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
                {t('roles.noRolesDescription')}
              </p>
              {hasPermission('roles:write') && (
                <Button
                  className="mt-4"
                  onClick={() => setIsCreateDialogOpen(true)}
                  leftIcon={<Plus className="h-4 w-4" />}
                >
                  {t('roles.addRole')}
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('roles.name')}</TableHead>
                  <TableHead>{t('roles.displayNameEn')}</TableHead>
                  <TableHead>{t('roles.displayNameAr')}</TableHead>
                  <TableHead>{t('roles.level')}</TableHead>
                  <TableHead>{t('roles.permissions')}</TableHead>
                  <TableHead>{t('fields.status')}</TableHead>
                  <TableHead className="ltr:text-right rtl:text-left">
                    {t('common:actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRoles.map((role) => (
                  <TableRow key={role._id}>
                    <TableCell className="font-medium">{role.name}</TableCell>
                    <TableCell>{role.displayNameEn}</TableCell>
                    <TableCell>{role.displayNameAr}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {role.level}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {role.permissions.length} {t('permissions.title').toLowerCase()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                          role.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        )}
                      >
                        {role.isActive ? t('common:active') : t('common:inactive')}
                      </span>
                    </TableCell>
                    <TableCell className="ltr:text-right rtl:text-left">
                      <Menu as="div" className="relative inline-block text-left">
                        <MenuButton
                          as={Button}
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                        >
                          <span className="sr-only">{t('common:actions')}</span>
                          <MoreVertical className="h-4 w-4" />
                        </MenuButton>
                        <MenuItems
                          className={cn(
                            'absolute z-10 mt-2 w-48 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
                            isRtl ? 'left-0' : 'right-0'
                          )}
                        >
                          <div className="py-1">
                            {hasPermission('roles:write') && (
                              <MenuItem>
                                {({ focus }) => (
                                  <button
                                    onClick={() => handleOpenEditDialog(role)}
                                    className={cn(
                                      'w-full flex items-center gap-2 px-4 py-2 text-sm',
                                      focus
                                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                                        : 'text-gray-700 dark:text-gray-300'
                                    )}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                    {t('roles.editRole')}
                                  </button>
                                )}
                              </MenuItem>
                            )}
                            {hasPermission('roles:delete') && (
                              <MenuItem>
                                {({ focus }) => (
                                  <button
                                    onClick={() => handleOpenDeleteDialog(role)}
                                    className={cn(
                                      'w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400',
                                      focus && 'bg-red-50 dark:bg-red-900/20'
                                    )}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    {t('roles.deleteRole')}
                                  </button>
                                )}
                              </MenuItem>
                            )}
                          </div>
                        </MenuItems>
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Role Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={handleCloseCreateDialog}>
        <DialogHeader onClose={handleCloseCreateDialog}>
          <DialogTitle>{t('roles.addRole')}</DialogTitle>
          <DialogDescription>{t('roles.subtitle')}</DialogDescription>
        </DialogHeader>
        <DialogContent className="max-h-[60vh] overflow-y-auto">
          <div className="space-y-4">
            <Input
              label={t('roles.name')}
              name="name"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              error={formErrors.name}
              isRequired
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('roles.displayNameEn')}
                name="displayNameEn"
                value={formData.displayNameEn}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, displayNameEn: e.target.value }))
                }
                error={formErrors.displayNameEn}
                isRequired
              />
              <Input
                label={t('roles.displayNameAr')}
                name="displayNameAr"
                value={formData.displayNameAr}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, displayNameAr: e.target.value }))
                }
                error={formErrors.displayNameAr}
                isRequired
              />
            </div>
            <Input
              label={t('roles.level')}
              name="level"
              type="number"
              min={1}
              max={100}
              value={formData.level.toString()}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, level: parseInt(e.target.value, 10) || 1 }))
              }
              error={formErrors.level}
              isRequired
              hint="1 = highest access, 100 = lowest"
            />
            <Input
              label={t('roles.description')}
              name="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
            />

            {renderPermissionMatrix()}

            <Checkbox
              id="isActive"
              label={t('fields.isActive')}
              checked={formData.isActive}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, isActive: checked }))
              }
            />
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseCreateDialog}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleCreateRole} isLoading={createRoleMutation.isPending}>
            {t('common:save')}
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={isEditDialogOpen} onClose={handleCloseEditDialog}>
        <DialogHeader onClose={handleCloseEditDialog}>
          <DialogTitle>{t('roles.editRole')}</DialogTitle>
          <DialogDescription>{t('roles.subtitle')}</DialogDescription>
        </DialogHeader>
        <DialogContent className="max-h-[60vh] overflow-y-auto">
          <div className="space-y-4">
            <Input
              label={t('roles.name')}
              name="name"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              error={formErrors.name}
              isRequired
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('roles.displayNameEn')}
                name="displayNameEn"
                value={formData.displayNameEn}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, displayNameEn: e.target.value }))
                }
                error={formErrors.displayNameEn}
                isRequired
              />
              <Input
                label={t('roles.displayNameAr')}
                name="displayNameAr"
                value={formData.displayNameAr}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, displayNameAr: e.target.value }))
                }
                error={formErrors.displayNameAr}
                isRequired
              />
            </div>
            <Input
              label={t('roles.level')}
              name="level"
              type="number"
              min={1}
              max={100}
              value={formData.level.toString()}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, level: parseInt(e.target.value, 10) || 1 }))
              }
              error={formErrors.level}
              isRequired
              hint="1 = highest access, 100 = lowest"
            />
            <Input
              label={t('roles.description')}
              name="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
            />

            {renderPermissionMatrix()}

            <Checkbox
              id="edit-isActive"
              label={t('fields.isActive')}
              checked={formData.isActive}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, isActive: checked }))
              }
            />
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseEditDialog}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleUpdateRole} isLoading={updateRoleMutation.isPending}>
            {t('common:save')}
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogHeader onClose={handleCloseDeleteDialog}>
          <DialogTitle>{t('roles.deleteRole')}</DialogTitle>
          <DialogDescription>{t('messages.confirmDeleteRole')}</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('messages.confirmDeleteRoleDescription')}
          </p>
          {selectedRole && (
            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {getRoleDisplayName(selectedRole)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {selectedRole.permissions.length} {t('permissions.title').toLowerCase()}
              </p>
            </div>
          )}
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseDeleteDialog}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteRole}
            isLoading={deleteRoleMutation.isPending}
          >
            {t('common:delete')}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
